import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSurveyForm } from '../hooks/useSurveyForm'
import {
  ProgressBar,
  StepIndicator,
  SectionHeader,
  TextQuestion,
  ChoiceQuestion,
  MatrixGroup,
  NavButtons,
  ThankYouScreen,
  ErrorAlert,
} from './ui'

// Helper to resolve a profile question's display text via i18n
function resolveProfileQuestion(q, t) {
  return {
    ...q,
    title: q.titleKey ? t(q.titleKey) : q.title,
    options: q.optionsKey ? t(q.optionsKey, { returnObjects: true }) : q.options,
  }
}

// Helper to resolve a matrix group's display text via i18n
function resolveMatrixGroup(group, t) {
  return {
    ...group,
    title: group.titleKey ? t(group.titleKey) : group.title,
    responseScale: group.responseScaleKey ? t(group.responseScaleKey, { returnObjects: true }) : group.responseScale,
    questions: group.questions.map(q => ({
      ...q,
      title: q.titleKey ? t(q.titleKey) : q.title,
    })),
  }
}

// Instructions step content
function InstructionsStep() {
  const { t } = useTranslation('survey')
  return (
    <>
      <SectionHeader number="!" title={t('sections.instructions')} />
      <div className="bg-white rounded-xl p-8 shadow-sm">
        <h3 className="font-display text-xl text-primary-600 mb-4">{t('beforeYouBegin')}</h3>
        <div className="text-gray-600 whitespace-pre-line leading-relaxed">
          {t('instructions')}
        </div>
      </div>
    </>
  )
}

// Profile step content
function ProfileStep({ questions, values, onChange, errors, readOnlyFields = {} }) {
  const { t } = useTranslation('survey')
  return (
    <>
      <SectionHeader
        number="1"
        title={t('sections.profile')}
        subtitle={t('profileSubtitle')}
      />
      {questions.map(q => {
        const resolved = resolveProfileQuestion(q, t)
        return resolved.type === 'text' ? (
          <TextQuestion
            key={resolved.id}
            question={resolved}
            value={values[resolved.id]}
            onChange={onChange}
            error={errors[resolved.id]}
            readOnly={!!readOnlyFields[resolved.id]}
          />
        ) : (
          <ChoiceQuestion
            key={resolved.id}
            question={resolved}
            value={values[resolved.id]}
            onChange={onChange}
            error={errors[resolved.id]}
            readOnly={!!readOnlyFields[resolved.id]}
          />
        )
      })}
    </>
  )
}

// Matrix step content
function MatrixStep({ step, values, onChange, errors }) {
  const { t } = useTranslation('survey')
  const sectionTitle = step.section === 'dyadic'
    ? t('sections.dyadic')
    : t('sections.personality')

  const stepNumber = step.section === 'dyadic' ? 2 : 3

  const resolvedGroup = resolveMatrixGroup(step.data, t)

  return (
    <>
      <SectionHeader
        number={stepNumber}
        title={sectionTitle}
        subtitle={t('matrixSubtitle')}
      />
      <MatrixGroup
        group={resolvedGroup}
        values={values}
        onChange={onChange}
        errors={errors}
      />
    </>
  )
}

// Save status indicator (shown for authenticated users only)
function SaveIndicator({ status }) {
  const { t } = useTranslation()
  if (!status) return null

  return (
    <div className={`text-xs font-medium flex items-center gap-1 transition-opacity ${
      status === 'saving' ? 'text-gray-400' :
      status === 'saved' ? 'text-green-500' :
      'text-red-400'
    }`}>
      {status === 'saving' && (
        <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeLinecap="round" />
        </svg>
      )}
      {status === 'saved' && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
      {status === 'error' && (
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      <span>
        {status === 'saving' ? t('buttons.saving') : status === 'saved' ? t('saveStatus.saved') : t('saveStatus.failed')}
      </span>
    </div>
  )
}

// Main form component
export default function SurveyForm({ options = {}, onCompleted }) {
  const { t } = useTranslation()
  const {
    currentStep,
    totalSteps,
    currentStepData,
    formValues,
    errors,
    isSubmitting,
    isSubmitted,
    submitError,
    progressPercent,
    isFirstStep,
    isLastStep,
    saveStatus,
    handleChange,
    goToNext,
    goToPrev,
    submitForm,
    resetForm,
  } = useSurveyForm(options)

  const readOnlyFields = options.readOnlyFields || {}

  // Handle next/submit
  const handleNext = () => {
    if (isLastStep) {
      submitForm()
    } else {
      goToNext()
    }
  }

  // Show thank you screen after submission
  if (isSubmitted) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-6">
        <Header />
        <ThankYouScreen onReset={onCompleted || resetForm} buttonLabel={onCompleted ? t('buttons.viewDashboard') : undefined} />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-6 pb-28">
      <Header />

      <ProgressBar percent={progressPercent} />

      <div className="relative">
        <StepIndicator
          current={currentStep + 1}
          total={totalSteps}
          sectionName={currentStepData.section !== 'intro' ? getSectionName(currentStepData.section, t) : null}
        />
        {options.userId && saveStatus && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <SaveIndicator status={saveStatus} />
          </div>
        )}
      </div>

      <ErrorAlert message={submitError} />

      {/* Step content */}
      <div className="animate-fadeIn">
        {currentStepData.type === 'instructions' && (
          <InstructionsStep />
        )}

        {currentStepData.type === 'profile' && (
          <ProfileStep
            questions={currentStepData.data}
            values={formValues}
            onChange={handleChange}
            errors={errors}
            readOnlyFields={readOnlyFields}
          />
        )}

        {currentStepData.type === 'matrix' && (
          <MatrixStep
            step={currentStepData}
            values={formValues}
            onChange={handleChange}
            errors={errors}
          />
        )}
      </div>

      <NavButtons
        onPrev={goToPrev}
        onNext={handleNext}
        showPrev={!isFirstStep}
        isLastStep={isLastStep}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}

// Header component
function Header() {
  const { t } = useTranslation('survey')
  return (
    <header className="text-center py-12 px-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl mb-8 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] animate-spin-slow">
          <div className="absolute inset-0 bg-gradient-radial from-white/20 to-transparent"></div>
        </div>
      </div>
      <h1 className="font-display text-3xl font-medium mb-2 relative">{t('title')}</h1>
      <p className="opacity-90 relative">{t('description')}</p>
    </header>
  )
}

// Helper function — uses survey namespace prefix for section names
function getSectionName(section, t) {
  const sectionNames = {
    profile: t('survey:sectionNames.profile'),
    dyadic: t('survey:sectionNames.dyadic'),
    personality: t('survey:sectionNames.personality'),
  }
  return sectionNames[section] || null
}
